import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Inbox, Mail, CalendarPlus, Radio, Wifi, WifiOff, Dot, Filter, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { getAccessToken } from "../../lib/api";
import { WsPageTitle } from "./WorkspaceLayout";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const h = diffMs / 3_600_000;
  if (h < 1) return `${Math.max(1, Math.round(diffMs / 60_000))} dk önce`;
  if (h < 24) return `${Math.round(h)} saat önce`;
  try { return d.toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

function wsUrl() {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const u = new URL(base, window.location.href);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.pathname = "/api/ws/admin/inbox";
  const token = getAccessToken();
  if (token) u.searchParams.set("token", token);
  return u.toString();
}

export default function Inbox_() {
  const qc = useQueryClient();
  const [kind, setKind] = useState("");
  const [wsConnected, setWsConnected] = useState(false);
  const [recentHits, setRecentHits] = useState([]); // id strings that got "new" flash
  const wsRef = useRef(null);
  const pingTimer = useRef(null);
  const reconnectTimer = useRef(null);

  const { data = [], isFetching, refetch } = useQuery({
    queryKey: ["inbox", kind],
    queryFn: () => api.get("/admin/inbox", { params: { kind, limit: 100 } }).then((r) => r.data),
  });
  const { data: counts } = useQuery({
    queryKey: ["inbox", "counts"],
    queryFn: () => api.get("/admin/inbox/counts").then((r) => r.data),
    refetchInterval: 30000,
  });

  // --- WebSocket live stream ----
  useEffect(() => {
    let closed = false;
    const connect = () => {
      if (closed) return;
      try {
        const ws = new WebSocket(wsUrl());
        wsRef.current = ws;
        ws.onopen = () => {
          setWsConnected(true);
          pingTimer.current = setInterval(() => {
            try { ws.send("ping"); } catch {}
          }, 25000);
        };
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "new" && msg.item) {
              setRecentHits((h) => [msg.item.id, ...h].slice(0, 10));
              qc.setQueryData(["inbox", kind], (prev = []) => {
                // De-dupe just in case the REST refetch also lands.
                if (prev.some((x) => x.id === msg.item.id && x.kind === msg.item.kind)) return prev;
                return [msg.item, ...prev].slice(0, 100);
              });
              qc.invalidateQueries({ queryKey: ["inbox", "counts"] });
            }
          } catch {}
        };
        ws.onclose = () => {
          setWsConnected(false);
          if (pingTimer.current) { clearInterval(pingTimer.current); pingTimer.current = null; }
          if (closed) return;
          reconnectTimer.current = setTimeout(connect, 3000);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {
        reconnectTimer.current = setTimeout(connect, 5000);
      }
    };
    connect();
    return () => {
      closed = true;
      if (pingTimer.current) clearInterval(pingTimer.current);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      try { wsRef.current?.close(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  return (
    <div data-testid="ws-inbox">
      <WsPageTitle
        icon={Inbox}
        title="Gelen Kutusu"
        sub="Mesajlar ve randevu talepleri bir arada, canlı olarak."
        action={
          <div className="flex items-center gap-2 text-xs">
            {wsConnected ? (
              <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-1" data-testid="inbox-ws-live">
                <Radio size={12} className="animate-pulse" />Canlı
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2.5 py-1" data-testid="inbox-ws-offline">
                <WifiOff size={12} />Bağlanıyor...
              </span>
            )}
            <button onClick={() => refetch()} className="p-1.5 rounded-full border hover:bg-stone-50" title="Yenile" data-testid="inbox-refresh">
              <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} />
            </button>
          </div>
        }
      />

      {/* Filter chips + counters */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {[["", "Tümü", data.length], ["appointment", "Talepler", counts?.pending_appointments ?? 0], ["message", "Mesajlar", counts?.unread_messages ?? 0]].map(([v, l, c]) => (
          <button
            key={v}
            onClick={() => setKind(v)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium transition ${
              kind === v
                ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                : "bg-white border-stone-200 text-stone-700 hover:bg-stone-50"
            }`}
            data-testid={`inbox-filter-${v || "all"}`}
          >
            <Filter size={11} />{l}
            {c > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${kind === v ? "bg-white/25" : "bg-indigo-100 text-indigo-800"}`}>{c}</span>}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <ul className="space-y-2">
        {data.map((item) => {
          const Icon = item.kind === "message" ? Mail : CalendarPlus;
          const grad = item.kind === "message" ? "from-sky-500 to-sky-700" : "from-rose-500 to-rose-700";
          const link = item.kind === "message" ? "/admin/workspace/mesajlar" : "/admin/workspace/talepler";
          const unread = item.kind === "message" ? !item.read : item.status === "pending" || item.status === "awaiting_approval";
          const isFresh = recentHits.includes(item.id);
          return (
            <li key={`${item.kind}:${item.id}`}>
              <Link
                to={link}
                className={`flex items-start gap-3 bg-white rounded-xl border p-3 shadow-sm hover:shadow-md transition group relative ${isFresh ? "ring-2 ring-amber-400 animate-pulse" : ""}`}
                data-testid={`inbox-item-${item.id}`}
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${grad} text-white flex items-center justify-center shrink-0 shadow`}>
                  <Icon size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900 truncate">{item.name}</p>
                    {unread && <Dot size={28} className="text-indigo-600 -mx-2" />}
                    {isFresh && <span className="text-[10px] bg-amber-100 text-amber-900 border border-amber-200 rounded-full px-1.5 py-0.5 font-semibold uppercase tracking-widest">yeni</span>}
                  </div>
                  {item.subject && <p className="text-xs font-medium text-stone-700 truncate">{item.subject}</p>}
                  {item.preview && <p className="text-xs text-stone-600 line-clamp-2 mt-0.5">{item.preview}</p>}
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-stone-500">
                    <span>{fmtDate(item.created_at)}</span>
                    {item.contact && <span className="truncate">· {item.contact}</span>}
                    {item.status && <span className="uppercase tracking-wide bg-stone-100 border border-stone-200 rounded px-1 py-0.5 text-[9px]">{item.status}</span>}
                  </div>
                </div>
              </Link>
            </li>
          );
        })}
        {data.length === 0 && (
          <li className="text-sm text-stone-400 italic p-8 bg-white rounded-xl border text-center">
            Henüz gelen bir şey yok. Mesaj ya da talep geldiğinde burada canlı olarak görünecek.
          </li>
        )}
      </ul>
    </div>
  );
}
